import { useState, type FormEvent } from "react";
import { LoginInput } from "./LoginInput";
import { PasswordInput } from "./PasswordInput";
import type { LoginValidationErrors } from "../types/login.types";
import styles from "./LoginPanel.module.css";

export interface LoginPanelProps {
  logoText?: string;
  userId: string;
  password: string;
  rememberId: boolean;
  loading: boolean;
  formError: string | null;
  fieldErrors: LoginValidationErrors;
  onUserIdChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberIdChange: (value: boolean) => void;
  onSubmit: () => void;
  onForgotPassword?: () => void;
  className?: string;
}

export function LoginPanel({
  logoText = "MES",
  userId,
  password,
  rememberId,
  loading,
  formError,
  fieldErrors,
  onUserIdChange,
  onPasswordChange,
  onRememberIdChange,
  onSubmit,
  onForgotPassword,
  className,
}: LoginPanelProps) {
  const [forgotHint, setForgotHint] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    onSubmit();
  };

  const handleForgot = () => {
    if (onForgotPassword) {
      onForgotPassword();
      return;
    }
    setForgotHint("비밀번호 재설정은 관리자에게 문의해 주세요.");
  };

  return (
    <div className={[styles.panel, className].filter(Boolean).join(" ")}>
      <div className={styles.brand}>
        <h1 className={styles.logo}>{logoText}</h1>
        <p className={styles.tagline}>Production, under control.</p>
        <p className={styles.description}>지능형 스마트 제조 관리 시스템</p>
        <div className={styles.brandDivider} aria-hidden>
          <span className={styles.brandDividerLine} />
          <span className={styles.brandDividerDot} />
          <span className={styles.brandDividerLine} />
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <h2 className={styles.formTitle}>로그인</h2>

        <div className={styles.fields}>
          <LoginInput
            id="login-user-id"
            label="아이디"
            placeholder="아이디"
            value={userId}
            onChange={(e) => onUserIdChange(e.target.value)}
            error={fieldErrors.userId}
            disabled={loading}
            autoFocus
          />
          <PasswordInput
            id="login-password"
            label="비밀번호"
            placeholder="비밀번호"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            error={fieldErrors.password}
            disabled={loading}
          />
        </div>

        <div className={styles.options}>
          <label className={styles.remember}>
            <input
              type="checkbox"
              checked={rememberId}
              onChange={(e) => onRememberIdChange(e.target.checked)}
              disabled={loading}
              aria-label="아이디 저장"
            />
            아이디 저장
          </label>
          <button
            type="button"
            className={styles.forgotLink}
            onClick={handleForgot}
            disabled={loading}
          >
            비밀번호 찾기
          </button>
        </div>

        <button
          type="submit"
          className={styles.submit}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className={styles.spinner} aria-hidden />
              <span>로그인 중…</span>
            </>
          ) : (
            "로그인"
          )}
        </button>

        {(formError || forgotHint) && (
          <p className={styles.formError} role="alert">
            {formError || forgotHint}
          </p>
        )}
      </form>
    </div>
  );
}
